import { FormEvent, useEffect, useRef, useState } from 'react';
import { Alert, TextField, Typography } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import LoginIcon from '@mui/icons-material/Login';
import { isAxiosError } from 'axios';
import { Location, useLocation, useNavigate } from 'react-router-dom';
import { apiErrorMessage } from 'api/axios_instance';
import useAuth from 'auth/useAuth';
import { LoginBackground, LoginCard, LoginForm, LoginWordmark } from './LoginView.styles';

const resolveErrorMessage = (error: unknown): string => {
  if (isAxiosError(error) && error.response?.status === 401) {
    return 'Invalid username or password.';
  }
  if (isAxiosError(error) && !error.response) {
    return 'Cannot reach the API. Check your connection and try again.';
  }
  return apiErrorMessage(error, 'Sign in failed. Please try again.');
};

const LoginView = () => {
  const { status, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const usernameRef = useRef<HTMLInputElement>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const redirectedFrom = (location.state as { from?: Location } | null)?.from?.pathname ?? '/';

  useEffect(() => {
    usernameRef.current?.focus();
  }, []);

  useEffect(() => {
    if (status === 'authenticated') {
      void navigate(redirectedFrom, { replace: true });
    }
  }, [status, redirectedFrom, navigate]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      await login({ username, password });
      void navigate(redirectedFrom, { replace: true });
    } catch (loginError) {
      setError(resolveErrorMessage(loginError));
      setPassword('');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <LoginBackground>
      <LoginCard>
        <div className="flex flex-col gap-2">
          <LoginWordmark aria-label="G.Tx">
            G<span className="gtx-dot">.</span>Tx
          </LoginWordmark>
          <Typography variant="body2" color="text.secondary">
            Sign in to continue to G.Tx Workflows.
          </Typography>
        </div>

        <LoginForm onSubmit={(event) => void handleSubmit(event)} noValidate>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label="Username"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            autoComplete="username"
            inputRef={usernameRef}
            required
            fullWidth
          />
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            required
            fullWidth
          />
          <LoadingButton
            type="submit"
            variant="contained"
            size="large"
            startIcon={<LoginIcon />}
            loading={isSubmitting}
            loadingPosition="start"
            disabled={!username || !password}
          >
            Sign in
          </LoadingButton>
        </LoginForm>
      </LoginCard>
    </LoginBackground>
  );
};

export default LoginView;
